/**
 * 기준정보 — 거래처등록
 * - 목록(헤더) + 계좌(1:N)
 * - 공통: 조회/추가/삭제/저장/엑셀
 * - 우측: 업로드 양식 다운로드 · 엑셀 업로드(평탄 1시트)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import {
  VENDOR_TYPE_OPTIONS,
  VENDOR_STATUS_OPTIONS,
  VENDOR_STATUS,
  ID_TYPE,
  ID_TYPE_OPTIONS,
  YN_OPTIONS,
  emptyVendor,
  emptyAccount,
  enrichVendorRow,
  filterVendors,
  accountsOfVendor,
  loadVendors,
  loadAccounts,
  saveVendorBundle,
  ensureVendorSeed,
  validateVendorBundle,
  nextVendorCode,
  nextSortOrderBy10,
  createVendorUid,
  createAccountUid,
  inferVendorTypeFromBizNo,
  normalizeIdNo,
  formatBizNo,
  downloadVendorExcelTemplate,
  readExcelAoaFromArrayBuffer,
  parseFlatVendorExcel,
  mergeUploadedVendors,
} from 'master/vendorStore';

const VENDOR_GRID_ID = 'master-vendor';
const ACCOUNT_GRID_ID = 'master-vendor-account';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || 1};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Meta = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
  flex-shrink: 0;
`;

function VendorRegisterInner() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const fileInputRef = useRef(null);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions(
    {}
  );

  const vendorRef = useRef(null);
  const accountRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [bundleAccounts, setBundleAccounts] = useState([]);
  const [hint, setHint] = useState('거래처를 선택하면 계좌가 표시됩니다.');

  const searchDefaults = useMemo(
    () => ({
      keyword: '',
      vendorType: '',
      status: VENDOR_STATUS.ACTIVE,
      useYn: 'Y',
    }),
    []
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues } = methods;

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  const vendorColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '거래처코드', field: 'vendorCode', size: 100, required: true }),
      hiCol.edTextColumn({ header: '거래처명', field: 'vendorName', size: 160, required: true }),
      hiCol.edComboColumn({
        header: '거래처구분',
        field: 'vendorType',
        size: 110,
        options: VENDOR_TYPE_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '식별유형',
        field: 'idType',
        size: 100,
        options: ID_TYPE_OPTIONS,
      }),
      hiCol.edTextColumn({
        header: '식별번호',
        field: 'idNo',
        size: 130,
        title: '사업자번호 입력 시 구분 자동 · 저장 시 숫자만 정규화',
      }),
      hiCol.edTextColumn({ header: '대표자', field: 'ceoName', size: 100 }),
      hiCol.edTextColumn({ header: '전화', field: 'tel', size: 110 }),
      hiCol.edTextColumn({ header: '이메일', field: 'email', size: 140 }),
      hiCol.edTextColumn({ header: '주소', field: 'address', size: 180 }),
      hiCol.edComboColumn({
        header: '상태',
        field: 'status',
        size: 90,
        options: VENDOR_STATUS_OPTIONS,
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 70, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 140 }),
    ],
    []
  );

  const accountColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '은행코드', field: 'bankCode', size: 90 }),
      hiCol.edTextColumn({ header: '은행명', field: 'bankName', size: 100 }),
      hiCol.edTextColumn({ header: '계좌번호', field: 'accountNo', size: 150, required: true }),
      hiCol.edTextColumn({ header: '예금주', field: 'accountHolder', size: 120 }),
      hiCol.edComboColumn({ header: '대표', field: 'mainYn', size: 70, options: YN_OPTIONS }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160 }),
    ],
    []
  );

  const vendorGrid = useHiGridHook({
    gridRef: vendorRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyVendor({ status: VENDOR_STATUS.ACTIVE, useYn: 'Y', idType: ID_TYPE.BIZ }),
  });

  const accountGrid = useHiGridHook({
    gridRef: accountRef,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyAccount({ mainYn: 'N', useYn: 'Y' }),
  });

  // 식별번호 변경 시 사업자번호면 거래처구분 자동
  useEffect(() => {
    vendorGrid.onAfterSaveCell = ({ data, field }) => {
      if (field !== 'idNo' && field !== 'idType') return;
      const row = data;
      if (!row || row.idType !== ID_TYPE.BIZ) return;
      const inferred = inferVendorTypeFromBizNo(row.idNo);
      if (!inferred) return;
      const api = vendorRef.current;
      const rows = api?.getData?.() || [];
      const next = rows.map((r) => {
        if (r !== row && r.vendorUid !== row.vendorUid) return r;
        return {
          ...r,
          vendorType: inferred.vendorType,
          partyKind: inferred.partyKind,
          idType: inferred.idType,
          vendorTypeSource: 'AUTO_BIZNO',
          idNo: normalizeIdNo(ID_TYPE.BIZ, r.idNo) || r.idNo,
          mod: r.mod === 'A' ? 'A' : 'U',
        };
      });
      api?.setData?.(next);
    };
  }, [vendorGrid]);

  const loadAccountsFor = useCallback(
    (uid) => {
      const list = accountsOfVendor(bundleAccounts, uid);
      accountRef.current?.setData?.(list);
      setHint(
        uid
          ? `계좌 ${list.length}건 · 대표계좌는 1개만`
          : '거래처를 선택하면 계좌가 표시됩니다.'
      );
    },
    [bundleAccounts]
  );

  const handleSearch = useCallback(async () => {
    await ensureVendorSeed(tenantCode);
    const vendors = await loadVendors(tenantCode);
    const accounts = await loadAccounts(tenantCode);
    setBundleAccounts(accounts);
    const filtered = filterVendors(vendors, getValues()).map(enrichVendorRow);
    vendorRef.current?.setData?.(filtered);
    const first = filtered[0];
    const uid = first?.vendorUid || '';
    setSelectedUid(uid);
    selectedUidRef.current = uid;
    // bundleAccounts state는 비동기 — 직접 accounts로 표시
    accountRef.current?.setData?.(accountsOfVendor(accounts, uid));
    setHint(uid ? `계좌 ${accountsOfVendor(accounts, uid).length}건` : '조회 결과 없음');
  }, [tenantCode, getValues]);

  useEffect(() => {
    handleSearch();
    // 최초 진입·기관 변경 시에만 자동 조회
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCode]);

  const onVendorSelect = useCallback(
    (row) => {
      const nextUid = row?.vendorUid || '';
      const prevUid = selectedUidRef.current;
      // 선택 전환 전 편집 중인 계좌를 메모리 번들에 반영 (미저장)
      if (prevUid && prevUid !== nextUid) {
        const editing = (accountRef.current?.getData?.() || []).map((r) => ({
          ...r,
          vendorUid: prevUid,
          accountUid: r.accountUid || createAccountUid(),
        }));
        setBundleAccounts((prev) => {
          const others = (prev || []).filter((a) => a.vendorUid !== prevUid);
          return [...others, ...editing];
        });
      }
      setSelectedUid(nextUid);
      selectedUidRef.current = nextUid;
    },
    []
  );

  // selectedUid·bundleAccounts 변경 시 계좌 그리드 갱신
  useEffect(() => {
    loadAccountsFor(selectedUid);
  }, [selectedUid, loadAccountsFor]);

  useEffect(() => {
    vendorGrid.onRowClicked = ({ data }) => onVendorSelect(data);
    vendorGrid.onSelectRow = ({ data }) => onVendorSelect(data);
  }, [vendorGrid, onVendorSelect]);

  const collectVendorsFromGrid = useCallback(() => {
    return (vendorRef.current?.getData?.() || []).map((r) => ({
      ...r,
      idNo: normalizeIdNo(r.idType, r.idNo) || digitsSafe(r.idNo),
      vendorUid: r.vendorUid || createVendorUid(),
    }));
  }, []);

  const collectAccountsFromGrid = useCallback(() => {
    const uid = selectedUidRef.current;
    const editing = (accountRef.current?.getData?.() || []).map((r) => ({
      ...r,
      vendorUid: uid,
      accountUid: r.accountUid || createAccountUid(),
    }));
    // 다른 거래처 계좌는 번들에서 유지
    const others = (bundleAccounts || []).filter((a) => a.vendorUid !== uid);
    return [...others, ...editing];
  }, [bundleAccounts]);

  const handleVendorAdd = useCallback(async () => {
    const data = vendorRef.current?.getData?.() || [];
    const code = nextVendorCode(data);
    const sortOrder = nextSortOrderBy10(data);
    await vendorGrid.handleAdd({
      ...emptyVendor({
        vendorUid: createVendorUid(),
        vendorCode: code,
        sortOrder,
        status: VENDOR_STATUS.ACTIVE,
        useYn: 'Y',
        idType: ID_TYPE.BIZ,
        mod: 'A',
      }),
    });
  }, [vendorGrid]);

  const handleAccountAdd = useCallback(async () => {
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('거래처를 먼저 선택하거나 추가하세요.');
      return;
    }
    const rows = accountRef.current?.getData?.() || [];
    const mainYn = rows.filter((r) => r.mod !== 'D').length === 0 ? 'Y' : 'N';
    const vendorRows = vendorRef.current?.getData?.() || [];
    const vendor = vendorRows.find((v) => v.vendorUid === uid);
    await accountGrid.handleAdd({
      ...emptyAccount({
        accountUid: createAccountUid(),
        vendorUid: uid,
        accountHolder: vendor?.vendorName || '',
        mainYn,
        useYn: 'Y',
        mod: 'A',
      }),
    });
  }, [accountGrid]);

  const handleSave = useCallback(async () => {
    // 선택 거래처 계좌를 번들에 반영한 뒤 검증
    const vendors = collectVendorsFromGrid();
    let accounts = collectAccountsFromGrid();

    // 그리드에 안 보이는(필터로 빠진) 기존 거래처도 유지
    const allStored = await loadVendors(tenantCode);
    const uidSet = new Set(vendors.map((v) => v.vendorUid));
    const mergedVendors = [
      ...vendors,
      ...allStored.filter((v) => !uidSet.has(v.vendorUid)),
    ];

    // 사업자번호 표시 정규화
    const normalized = mergedVendors.map((v) => {
      const idNo = normalizeIdNo(v.idType, v.idNo);
      return {
        ...v,
        idNo,
        idNoDisp: v.idType === ID_TYPE.BIZ ? formatBizNo(idNo) : idNo,
      };
    });

    const errors = validateVendorBundle(normalized, accounts);
    // PoC: 체크디지트 실패는 경고만 (시드·이관 완화) — 중복·필수만 차단
    const blocking = errors.filter((e) => !e.includes('사업자번호 형식'));
    const warnings = errors.filter((e) => e.includes('사업자번호 형식'));
    if (blocking.length) {
      showAlert(blocking.slice(0, 8).join('\n'));
      return;
    }
    if (warnings.length) {
      const ok = await showConfirm(
        `${warnings.slice(0, 5).join('\n')}\n\n형식 경고가 있습니다. 그래도 저장할까요?`
      );
      if (!ok) return;
    }

    await saveVendorBundle(tenantCode, normalized, accounts);
    setBundleAccounts(accounts);
    showAlert('저장되었습니다.');
    await handleSearch();
  }, [tenantCode, collectVendorsFromGrid, collectAccountsFromGrid, handleSearch]);

  const handleTemplateDownload = useCallback(() => {
    downloadVendorExcelTemplate('거래처업로드양식.xlsx');
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUploadFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const buf = await file.arrayBuffer();
        const aoa = readExcelAoaFromArrayBuffer(buf);
        const parsed = parseFlatVendorExcel(aoa, { tenantCode });
        if (parsed.errors?.length) {
          showAlert(parsed.errors.slice(0, 8).join('\n'));
          if (!parsed.vendors.length) return;
        }
        if (!parsed.vendors.length) {
          showAlert('업로드할 거래처가 없습니다.');
          return;
        }
        const ok = await showConfirm(
          `거래처 ${parsed.vendors.length}건 · 계좌 ${parsed.accounts.length}건을 반영할까요?\n(동일 식별번호는 갱신, 신규는 추가)`
        );
        if (!ok) return;

        await ensureVendorSeed(tenantCode);
        const existingV = await loadVendors(tenantCode);
        const existingA = await loadAccounts(tenantCode);
        const merged = mergeUploadedVendors(
          existingV,
          existingA,
          parsed.vendors,
          parsed.accounts
        );
        const errors = validateVendorBundle(merged.vendors, merged.accounts);
        const blocking = errors.filter((err) => !err.includes('사업자번호 형식'));
        if (blocking.length) {
          showAlert(`검증 실패\n${blocking.slice(0, 8).join('\n')}`);
          return;
        }
        await saveVendorBundle(tenantCode, merged.vendors, merged.accounts);
        setBundleAccounts(merged.accounts);
        showAlert('업로드·저장이 완료되었습니다.');
        await handleSearch();
      } catch (err) {
        showAlert(`엑셀 읽기 실패: ${err?.message || err}`);
      }
    },
    [tenantCode, handleSearch]
  );

  const getVendorExportData = useCallback(
    () =>
      (vendorRef.current?.getFilteredData?.() ?? vendorRef.current?.getData?.() ?? []).filter(
        (r) => r.mod !== 'D'
      ),
    []
  );
  const getVendorExportCols = useCallback(
    () => vendorRef.current?.getDataColumns?.() ?? vendorColumns,
    [vendorColumns]
  );
  const getAccountExportData = useCallback(
    () =>
      (accountRef.current?.getFilteredData?.() ?? accountRef.current?.getData?.() ?? []).filter(
        (r) => r.mod !== 'D'
      ),
    []
  );
  const getAccountExportCols = useCallback(
    () => accountRef.current?.getDataColumns?.() ?? accountColumns,
    [accountColumns]
  );

  useRegisterGridExport({
    id: VENDOR_GRID_ID,
    label: '거래처',
    getData: getVendorExportData,
    getDataColumns: getVendorExportCols,
    joinKeys: ['vendorUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: ACCOUNT_GRID_ID,
    label: '계좌',
    getData: getAccountExportData,
    getDataColumns: getAccountExportCols,
    joinKeys: ['vendorUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '거래처등록',
  });

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'keyword',
          label: '검색어',
          type: 'text',
          width: '200px',
          labelWidth: SEARCH_LABEL_W,
          placeholder: '코드·명·번호·대표자',
        },
        {
          name: 'vendorType',
          label: '구분',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...VENDOR_TYPE_OPTIONS],
          width: '140px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'status',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...VENDOR_STATUS_OPTIONS],
          width: '120px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'useYn',
          label: '사용',
          type: 'select',
          options: [
            { value: '', label: '전체' },
            ...YN_OPTIONS,
          ],
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      {
        id: 'excel',
        label: '엑셀',
        icon: '📥',
        onClick: openExcelDialog,
        hotkey: 'F11',
        title: '엑셀 다운로드 (F11)',
      },
    ],
    [
      handleSearch,
      handleActiveAdd,
      handleActiveDelete,
      handleSave,
      openExcelDialog,
      activeLabel,
    ]
  );

  const extraButtons = useMemo(
    () => [
      {
        id: 'tpl',
        label: '양식다운로드',
        icon: '📄',
        onClick: handleTemplateDownload,
        title: '평탄 1시트 업로드 양식 (목록+계좌)',
      },
      {
        id: 'upload',
        label: '엑셀업로드',
        icon: '📤',
        onClick: handleUploadClick,
        title: '양식과 동일한 평탄 시트로 일괄 반영',
      },
    ],
    [handleTemplateDownload, handleUploadClick]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <CollapsibleSearchPanel
          storageKey="master-vendor-register"
          prefsUserId={prefsUserId}
          title="조회조건"
        >
          <FormArea compact fields={searchFields} onFieldEnter={handleSearch} />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성: <strong style={{ color: 'var(--erp-color-primary)' }}>{activeLabel || '—'}</strong>
        {' · '}
        Alt+F2 그리드 전환 · F8/F9는 활성 그리드
        {' · '}
        사업자번호 입력 시 거래처구분 자동
        {' · '}
        {hint}
      </Meta>

      <Stack>
        <Panel $flex={1.35}>
          <ActiveGridHeader gridId={VENDOR_GRID_ID} extra="식별번호·구분 · 계좌는 하단">
            거래처
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={VENDOR_GRID_ID}
              label="거래처"
              order={0}
              onAdd={handleVendorAdd}
              onDelete={async () => vendorGrid.handleDelete?.()}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={VENDOR_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={vendorRef}
                  columns={vendorColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === VENDOR_GRID_ID}
                  {...vendorGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="거래처"
                  gridId={VENDOR_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={0.85}>
          <ActiveGridHeader gridId={ACCOUNT_GRID_ID} extra="동일 사업자 다계좌 · 대표 1개">
            계좌
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={ACCOUNT_GRID_ID}
              label="계좌"
              order={10}
              onAdd={handleAccountAdd}
              onDelete={async () => accountGrid.handleDelete?.()}
              canAdd={!!selectedUid}
              canDelete={!!selectedUid}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={ACCOUNT_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={accountRef}
                  columns={accountColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={48}
                  showModColumn
                  headerActive={activeGridId === ACCOUNT_GRID_ID}
                  {...accountGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableFreeze
                  enableQuickExport
                  exportFileName="거래처계좌"
                  gridId={ACCOUNT_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleUploadFile}
      />
      {excelDialog}
    </PageContainer>
  );
}

function digitsSafe(v) {
  return String(v || '').trim();
}

export default function VendorRegister() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <VendorRegisterInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
